import { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';

const DataContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DataProvider = ({ children }) => {
  const [content, setContent] = useState({});
  const [courses, setCourses] = useState([]);
  const [settings, setSettings] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchContent = useCallback(async (page = null) => {
    setLoading(true);
    try {
      const url = page ? `${API}/content/${page}` : `${API}/content`;
      const response = await axios.get(url);
      
      if (page) {
        setContent(prev => ({ ...prev, [page]: response.data }));
      } else {
        // Group by page
        const grouped = response.data.reduce((acc, item) => {
          if (!acc[item.page]) acc[item.page] = [];
          acc[item.page].push(item);
          return acc;
        }, {});
        setContent(grouped);
      }
      return response.data;
    } catch (error) {
      console.error('Error fetching content:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCourses = useCallback(async (activeOnly = false) => {
    setLoading(true);
    try {
      const url = activeOnly ? `${API}/courses/active` : `${API}/courses`;
      const response = await axios.get(url);
      setCourses(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching courses:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching settings:', error);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGallery = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/gallery`);
      setGallery(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching gallery:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const getContentBySection = (page, section) => {
    const pageContent = content[page] || [];
    return pageContent.find(item => item.section === section);
  };

  const getContentByPage = (page) => {
    return content[page] || [];
  };

  return (
    <DataContext.Provider value={{
      content,
      courses,
      settings,
      gallery,
      loading,
      fetchContent,
      fetchCourses,
      fetchSettings,
      fetchGallery,
      getContentBySection,
      getContentByPage
    }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};
